import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import { convertUTCDateToLocalDate, formatDateShort, formatDateTime } from '../../utils/formatUtils';

import { Box, Divider, Paper as MuiPaper, Stack, Typography, TypographyProps } from '@mui/material';
import { spacing } from '@mui/system';

import Loader from '../../components/common/Loader';
import PageHeader from '../../components/common/PageHeader';
import { getWorkOrder, PoolInfo, WorkOrder } from '../../apis/serviceHistoryApi';
import PictureList from '../../components/common/PictureList';
import ServiceEntryTable from './components/ServiceEntryTable';
import ChecklistTable from './components/ChecklistTable';

const Paper = styled(MuiPaper)(spacing);

const sortPools = (a: PoolInfo, b: PoolInfo) => {
  const firstPoolName = a.name ?? '';
  const secondPoolName = b.name ?? '';
  if (firstPoolName < secondPoolName) {
    return -1;
  }
  if (firstPoolName > secondPoolName) {
    return 1;
  }
  return 0;
};

const InfoLine: React.FC<TypographyProps> = (props: TypographyProps) => {
  return <Typography component="span" variant="h6" {...props} fontWeight={400}></Typography>;
};

const Heading: React.FC<TypographyProps> = (props: TypographyProps) => {
  return <Typography variant="h4" pl={1} pt={4} pb={0} {...props}></Typography>;
};

type WorkOrderInfoProps = {
  workOrder: WorkOrder;
};

const WorkOrderInfo: React.FC<WorkOrderInfoProps> = ({ workOrder: workOrder }) => {
  return (
    <Box pt={5}>
      <Paper px={1}>
        <Box sx={{ lineHeight: 2 }}>
          <Box>
            <InfoLine>
              Service Date: &nbsp;
              {formatDateTime(convertUTCDateToLocalDate(workOrder.completeTime))}
            </InfoLine>
          </Box>
          <Box>
            <InfoLine>{workOrder.address}</InfoLine>
          </Box>
          {(workOrder.serviceTech?.firstName || workOrder.serviceTech?.lastName) && (
            <Box>
              <InfoLine>
                Tech: &nbsp;
                {workOrder.serviceTech?.firstName} {workOrder.serviceTech?.lastName}
              </InfoLine>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

function WorkOrderDetail() {
  const { workOrderId: workOrderId } = useParams();
  const [workOrder, setWorkOrder] = React.useState<WorkOrder | null>(null);
  const [pools, setPools] = React.useState<PoolInfo[]>([]);
  const [checklistPictureId, setChecklistPictureId] = React.useState<string | null>(null);

  useEffect(() => {
    getWorkOrder(workOrderId!).then((result) => {
      setWorkOrder(result);
      setPools(result.pools.sort(sortPools));
    });
  }, [workOrderId]);

  const showChecklistPicture = (pictureId: string) => {
    setChecklistPictureId(pictureId);
  };

  const onChecklistImageClosed = () => setChecklistPictureId(null);

  return (
    <>
      {!workOrder && <Loader></Loader>}
      {workOrder && (
        <>
          <PageHeader
            title="Work Order"
            heading={`Work Order: ${formatDateShort(convertUTCDateToLocalDate(workOrder.completeTime))}`}
            backRef="/service-history?fromDetail=1"
          ></PageHeader>

          <Paper mt={5} px={6} pb={5} sx={{ minWidth: 200 }}>
            <Stack spacing={1}>
              <WorkOrderInfo workOrder={workOrder} />
              {workOrder.pictures.length > 0 && (
                <>
                  <Heading>
                    Photos
                    <Box pl={3} display="inline-block" fontWeight={400} color="text.secondary">
                      Press to view
                    </Box>
                  </Heading>
                  <Divider sx={{ pt: 1 }}></Divider>
                  <PictureList pictures={workOrder.pictures} selectedPictureId={checklistPictureId} onImageClosed={onChecklistImageClosed} />
                </>
              )}
              {!!workOrder.checklistItems?.length && (
                <>
                  <Heading>Work Performed</Heading>
                  <Divider sx={{ pt: 1 }}></Divider>
                  <Box width="100%" mr={2} mb={2}>
                    <ChecklistTable entries={workOrder.checklistItems} fullWidth={true} onPictureClicked={showChecklistPicture}></ChecklistTable>
                  </Box>
                </>
              )}
              {pools
                .filter((pool) => pool.readings || pool.dosages || pool.calculatorEntries)
                .map((pool, index) => (
                  <Box key={`pool_${index}`}>
                    <Heading>{pool.name} Service Info</Heading>
                    <Divider sx={{ pt: 1 }}></Divider>
                    <Box display="flex" flexWrap="wrap">
                      {(pool.readings || pool.calculatorEntries) && (
                        <Box width={300} mr={2}>
                          <ServiceEntryTable entries={pool.readings} heading="Readings"></ServiceEntryTable>
                          <ServiceEntryTable entries={pool.calculatorEntries} heading="Water Balance"></ServiceEntryTable>
                        </Box>
                      )}
                      {pool.dosages && (
                        <Box width={300} mr={2}>
                          <ServiceEntryTable entries={pool.dosages} heading="Chemicals Added"></ServiceEntryTable>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
            </Stack>
          </Paper>
        </>
      )}
    </>
  );
}

export default WorkOrderDetail;
